  // src/Settings/Settings.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService, enterpriseService, shortcutService, biometricService } from '../services';
import './Settings.css';
import SkeletonManager from '../components/loaders/SkeletonManager';
import Button from '../components/buttons/Button';
import BackButton from '../components/buttons/BackButton';
import Card from '../components/Card/Card';
import { createLogger } from '../services/Debug';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import Toggle from '../components/Toggle/Toggle';
import LanguagesList from './LanguagesList';

import { isElectron, isCapacitor } from '../utils/platform';
import useNotification from '../components/Notifications/useNotifications.js';
import Notification from '../components/Notifications/Notification.js';
import Input from '../components/inputs/Input';
import { useAuth } from '../context/AuthContext';

import { SPECIALTIES } from '../constants/specialties';

// Icons
import {
  FaUser,
  FaLanguage,
  FaCog,
  FaBuilding,
  FaSignOutAlt,
  FaTrash,
  FaCrown,
  FaPlus,
  FaEdit,
  FaTimes,
  FaKey,
  FaLock,
  FaSpinner,
  FaCheck,
  FaUsers,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaEnvelope,
  FaGlobe,
  FaMicrophone,
  FaStethoscope,
  FaSkull,
  FaExclamationTriangle,
  FaKeyboard,
  FaSave,
  FaUndo,
  FaInfoCircle,
  FaBan,
  FaPaperPlane,
  FaPhone
} from 'react-icons/fa';

function Settings({ handleLogout }) {
  const log = createLogger('Settings');
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  const { authReady, profile, refreshProfile, phoneNumber } = useAuth();
  
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // Form fields - initialized from profile
  const [languagePreference, setLanguagePreference] = useState('fr');
  const [dictationLanguage, setDictationLanguage] = useState('fr');
  
  // Specialty fields
  const [specialization, setSpecialization] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [showCustomSpecialty, setShowCustomSpecialty] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState('');

  // Email reports preference
  const [emailReportsEnabled, setEmailReportsEnabled] = useState(false);
  
  // Enterprises
  const [enterprises, setEnterprises] = useState([]);
  const [leavingEnterprise, setLeavingEnterprise] = useState(null);
  const [settingActiveEnterprise, setSettingActiveEnterprise] = useState(null);
  
  // Account deletion
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteAccountForm, setDeleteAccountForm] = useState({ phone: '', code: '', pin: '' });
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  
  // Advanced sections toggles

  const [showAdvancedShortcuts, setShowAdvancedShortcuts] = useState(false);
  
  // Sessions management
  const [showSessionsSection, setShowSessionsSection] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [revokingSessionId, setRevokingSessionId] = useState(null);
   
  // Shortcuts state (Electron only)
  const [shortcuts, setShortcuts] = useState({});
  const [defaultShortcuts] = useState({
    toggleApp: { key: 'F12', desc: 'Afficher/masquer voxia', disabled: false },
    openMacroSearch: { key: 'Alt+Space', desc: 'Ouvrir la recherche de macros', disabled: false },
    toggleRecord: { key: 'F5', desc: 'Dictaphone: enregistrer/pause', disabled: false },
    toggleShowRecorder: { key: 'F6', desc: 'Dictaphone: afficher/masquer la fenêtre', disabled: false },
    toggleLiveRecord: { key: 'F9', desc: 'Dictée Live: enregistrer/pause', disabled: false },
    toggleLiveFormat: { key: 'F8', desc: 'Dictée Live: basculer « Mettre en forme »', disabled: false },
    liveTextareaInsert: { key: 'Insert', desc: 'Dictée Live: insérer le contenu du texte', disabled: false },
    screenshotScreen1: { key: 'Ctrl+Shift+1', desc: 'Capture d\'écran comme annexe - Écran 1', disabled: false },
    screenshotScreen2: { key: 'Ctrl+Shift+2', desc: 'Capture d\'écran comme annexe - Écran 2', disabled: false },
    screenshotScreen3: { key: 'Ctrl+Shift+3', desc: 'Capture d\'écran comme annexe - Écran 3', disabled: false },
    annexCopy: { key: 'CommandOrControl+Shift+C', desc: 'Ajouter le contenu du presse-papier comme annexe', disabled: false }
  });
  const [recordingShortcut, setRecordingShortcut] = useState(null);
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(true);
  const [shortcutsModified, setShortcutsModified] = useState(false);
  const recordingShortcutRef = useRef(null);
  const keyDownHandlerRef = useRef(null);

  // Primary shortcuts that are always visible
  const primaryShortcutIds = ['toggleApp', 'openMacroSearch', 'toggleRecord'];

  // Change email state
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [changeEmailForm, setChangeEmailForm] = useState({
    newEmail: '',
    pin: '',
    code: '',
    emailCode: ''
  });
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState(null);
  const [changeEmailSuccess, setChangeEmailSuccess] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  // Change phone state
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [changePhoneForm, setChangePhoneForm] = useState({
    newPhone: '',
    emailCode: '',
    newPhoneCode: '',
    pin: ''
  });
  const [changePhoneLoading, setChangePhoneLoading] = useState(false);
  const [changePhoneError, setChangePhoneError] = useState(null);
  const [changePhoneSuccess, setChangePhoneSuccess] = useState(false);
  const [phoneEmailCodeSent, setPhoneEmailCodeSent] = useState(false);

  // Trusted devices state
  const [showTrustedDevices, setShowTrustedDevices] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [trustedDevicesLoading, setTrustedDevicesLoading] = useState(false);
  const [removingDeviceId, setRemovingDeviceId] = useState(null);


  // Sync UI fields when profile is ready
  useEffect(() => {
    if (!authReady || !profile) return;
    setLanguagePreference(profile.languagePreference || 'français');
    setDictationLanguage(profile.dictationLanguage || 'fr');
    setSpecialization(profile.specialty || '');
    setEnterprises(profile.enterprises || []);
    setSpecialties(SPECIALTIES);
    setEmailReportsEnabled(profile.emailReportsEnabled ?? false);

  }, [authReady, profile]);

  // Ensure a minimum 500ms initial loading experience
  useEffect(() => {
    setIsInitialLoading(true);
    const timer = setTimeout(() => setIsInitialLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);


  // Fetch trusted devices and sessions on page load
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch trusted devices
      try {
        const devices = await biometricService.getTrustedDevices();
        setTrustedDevices(Array.isArray(devices) ? devices : []);
      } catch (err) {
        log.error('Error fetching trusted devices:', err);
        setTrustedDevices([]);
      }

      // Fetch sessions
      try {
        const data = await userService.getSessions();
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        log.error('Error fetching sessions:', err);
        setSessions([]);
      }
    };

    if (authReady && profile) {
      loadInitialData();
    }
  }, [authReady, profile]);

  // Load shortcuts for Electron from backend
  useEffect(() => {
    if (!isElectron) return;
    (async () => {
      try {
        // Prefer backend source
        const platformShortcuts = await shortcutService.getPlatformShortcuts();
        // Merge with defaults and drop deprecated keys
        const merged = { ...defaultShortcuts };
        Object.keys(platformShortcuts || {}).forEach(key => {
          if (/^insertSection\d+$/.test(key)) return;
          if (key === 'dictationServer') return;
          if (key === 'toggleLive' || key === 'stopAndSendLive') return;
          const s = platformShortcuts[key];
          if (s) {
            // Accept explicit disabled flag from backend; if missing, infer from absence of key
            const disabled = s.disabled === true || !s.key;
            merged[key] = { desc: s.desc || merged[key]?.desc, key: disabled ? '' : s.key || merged[key]?.key || '', disabled };
          }
        });
        // Migration: adopt legacy live keys into toggleLiveRecord if not set
        if (!merged.toggleLiveRecord?.key) {
          const k = platformShortcuts?.toggleLive?.key || platformShortcuts?.stopAndSendLive?.key || '';
          if (k) merged.toggleLiveRecord = { desc: merged.toggleLiveRecord?.desc || 'Dictée Live: démarrer/arrêter et envoyer', key: k, disabled: false };
        }
        setShortcuts(merged);
        try { localStorage.setItem('electronShortcutsMap', JSON.stringify(merged)); } catch (_) {}
        setAutoSelectEnabled(true);
        // Also push to Electron so accelerators reflect backend config
        try { await window.electronAPI?.setShortcuts?.(merged); } catch (_) {}
      } catch (err) {
        // Fallback to Electron store if backend fails
        if (window.electronAPI) {
          await loadShortcuts();
        } else {
          // Fallback to localStorage if available
          try {
            const local = JSON.parse(localStorage.getItem('electronShortcutsMap') || 'null');
            if (local && typeof local === 'object') {
              setShortcuts({ ...defaultShortcuts, ...local });
            } else {
              setShortcuts(defaultShortcuts);
            }
          } catch (_) {
            setShortcuts(defaultShortcuts);
          }
        }
      }
    })();
  }, []);

  // Manual refresh handler with 500ms minimum loading
  const handleRefreshProfile = async () => {
    setIsInitialLoading(true);
    const start = Date.now();
    try {
      await refreshProfile();
    } catch (_) {
      // ignore
    } finally {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 500 - elapsed);
      setTimeout(() => setIsInitialLoading(false), remaining);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (keyDownHandlerRef.current) {
        document.removeEventListener('keydown', keyDownHandlerRef.current, true);
      }
      // Ensure we fully exit recording mode and re-enable shortcuts if unmounting mid-recording
      if (recordingShortcutRef.current) {
        stopRecordingShortcut();
      }
    };
  }, []);

  // Load shortcuts from Electron
  const loadShortcuts = async () => {
    try {
      const loadedShortcuts = await window.electronAPI.getShortcuts() || {};
      const merged = { ...defaultShortcuts };
      Object.keys(loadedShortcuts).forEach(key => {
        // Skip deprecated insertSection shortcuts (Ctrl/Cmd + 0..9)
        if (/^insertSection\d+$/.test(key)) return;
        // Drop deprecated dictationServer shortcut in UI
        if (key === 'dictationServer') return;
        // Drop legacy live shortcuts in UI
        if (key === 'toggleLive' || key === 'stopAndSendLive') return;
        if (loadedShortcuts[key]) {
          const s = loadedShortcuts[key];
          const disabled = s.disabled === true || !s.key;
          merged[key] = { desc: s.desc || merged[key]?.desc, key: disabled ? '' : s.key || merged[key]?.key || '', disabled };
        }
      });
      // Migration: if toggleLiveRecord empty, adopt legacy key if present
      if (!merged.toggleLiveRecord?.key) {
        const k = loadedShortcuts?.toggleLive?.key || loadedShortcuts?.stopAndSendLive?.key || '';
        if (k) merged.toggleLiveRecord = { desc: merged.toggleLiveRecord?.desc || 'Dictée Live: démarrer/arrêter et envoyer', key: k, disabled: false };
      }
      setShortcuts(merged);
      
      // Auto-select feature has been removed from Electron, keeping UI for future use
      // Just use local state for now
      setAutoSelectEnabled(true);
    } catch (error) {
      log.error('Error loading shortcuts:', error);
      setShortcuts(defaultShortcuts);
    }
  };


  // Start recording a shortcut
  const startRecordingShortcut = (shortcutId, force = false) => {
    if (!force && shortcuts[shortcutId]?.disabled) return; // do not record when disabled unless forced
    // Stop any previous recording
    if (recordingShortcutRef.current) {
      stopRecordingShortcut();
    }
    
    setRecordingShortcut(shortcutId);
    recordingShortcutRef.current = shortcutId;
    
    // Temporarily disable global shortcuts in Electron while waiting for key
    try { window.electronAPI?.suspendGlobalShortcuts?.(true); } catch (_) {}
    try { window.electronAPI?.disableGlobalShortcuts?.(); } catch (_) {}
    
    // Create handler with current shortcutId in closure
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Allow ESC to cancel recording; if no key selected, mark disabled
      if (e.key === 'Escape') {
        stopRecordingShortcut({ ensureDisabledIfEmpty: true });
        return;
      }
      
      const keys = [];
      if (e.ctrlKey || e.metaKey) keys.push('CommandOrControl');
      if (e.altKey) keys.push('Alt');
      if (e.shiftKey) keys.push('Shift');
      
      let key = e.key;
      const keyMap = {
        ' ': 'Space',
        'ArrowUp': 'Up',
        'ArrowDown': 'Down',
        'ArrowLeft': 'Left',
        'ArrowRight': 'Right',
        'Enter': 'Return',
        'Escape': 'Escape',
        'Tab': 'Tab',
        'Delete': 'Delete',
        'Backspace': 'Backspace',
        'Home': 'Home',
        'End': 'End',
        'PageUp': 'PageUp',
        'PageDown': 'PageDown'
      };
      
      if (e.key.match(/^F\d+$/)) {
        key = e.key;
      } else if (keyMap[e.key]) {
        key = keyMap[e.key];
      } else if (e.key.length === 1) {
        key = e.key.toUpperCase();
      }
      
      // Skip if only modifier keys
      if (['Control', 'Alt', 'Shift', 'Meta', 'Command'].includes(e.key)) {
        return;
      }
      
      keys.push(key);
      const newShortcut = keys.join('+');
      
      // Update shortcuts with the new key
      setShortcuts(prev => ({
        ...prev,
        [shortcutId]: { ...prev[shortcutId], key: newShortcut, disabled: false }
      }));
      
      setShortcutsModified(true);
      
      // Stop recording after a short delay to ensure state update
      setTimeout(() => {
        stopRecordingShortcut();
      }, 100);
    };
    
    keyDownHandlerRef.current = handler;
    document.addEventListener('keydown', handler, true);
  };

  // Stop recording
  const stopRecordingShortcut = (options = {}) => {
    const ensureDisabledIfEmpty = !!options.ensureDisabledIfEmpty;
    // If requested, and no key was selected for the currently recording id, mark it disabled
    try {
      const id = recordingShortcutRef.current;
      if (ensureDisabledIfEmpty && id) {
        const current = shortcuts[id];
        if (!current?.key) {
          setShortcuts(prev => ({
            ...prev,
            [id]: { ...prev[id], key: '', disabled: true }
          }));
          setShortcutsModified(true);
        }
      }
    } catch (_) {}

    if (keyDownHandlerRef.current) {
      document.removeEventListener('keydown', keyDownHandlerRef.current, true);
      keyDownHandlerRef.current = null;
    }
    // Re-enable global shortcuts in Electron
    try { window.electronAPI?.suspendGlobalShortcuts?.(false); } catch (_) {}
    try { window.electronAPI?.enableGlobalShortcuts?.(); } catch (_) {}
    setRecordingShortcut(null);
    recordingShortcutRef.current = null;
  };

  // Save shortcuts
  const saveShortcuts = async () => {
    try {
      // Update Electron accelerators
      await window.electronAPI.setShortcuts(shortcuts);
      // Persist to backend for this platform
      const platform = shortcutService.detectPlatform();
      // Build platform map (only keys currently present in UI)
      const platformMap = {};
      Object.entries(shortcuts).forEach(([id, def]) => {
        if (!/^insertSection\d+$/.test(id) && id !== 'dictationServer' && def?.key) {
          platformMap[id] = { key: def.key, desc: def.desc, disabled: !!def.disabled };
        }
      });
      // Also include explicitly disabled shortcuts with empty key so backend knows they are disabled
      Object.entries(shortcuts).forEach(([id, def]) => {
        if (!/^insertSection\d+$/.test(id) && id !== 'dictationServer' && (!def?.key || def?.disabled)) {
          platformMap[id] = { key: '', desc: def.desc, disabled: true };
        }
      });
      await shortcutService.replacePlatformShortcuts(platform, platformMap);
      try { localStorage.setItem('electronShortcutsMap', JSON.stringify(shortcuts)); } catch (_) {}
      // Auto-select feature has been removed from Electron API
      // If needed in future, implement setAutoSelectEnabled in preload.js
      setShortcutsModified(false);
      showNotification('Raccourcis enregistrés avec succès', 'success');
    } catch (error) {
      showNotification('Erreur lors de l\'enregistrement des raccourcis', 'error');
    }
  };

  // Reset shortcuts to defaults
  const resetShortcuts = () => {
    setShortcuts(defaultShortcuts);
    setAutoSelectEnabled(true);
    setShortcutsModified(true);
  };

  const isWithinUsageLimit = (usageType) => {
    return profile?.isWithinUsageLimit(usageType) ?? true;
  };

  

  // Handle specialty field changes
  const handleSpecialtyFieldChange = async (field, value) => {
    try {
      const updates = {
        profile: {
          [field]: value
        }
      };
      
      await userService.updateProfile(updates);
      await refreshProfile();
      setSpecialization(value);
     
      
      showNotification(`Spécialité mis à jour`, 'success');
    } catch (error) {
      log.error(`Error updating ${field}:`, error);
      showNotification('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleSpecializationChange = async (e) => {
    const value = e.target.value;
    
    if (value === 'other') {
      setShowCustomSpecialty(true);
      setSpecialization('');
    } else {
      setShowCustomSpecialty(false);
      setSpecialization(value);
      await handleSpecialtyFieldChange('specialty', value);
    }
  };

  const handleCustomSpecialtyChange = (e) => {
    const value = e.target.value;
    setCustomSpecialty(value);
    setSpecialization(value);
  };

  const handleCustomSpecialtyBlur = async () => {
    if (customSpecialty.trim()) {
      await handleSpecialtyFieldChange('specialty', customSpecialty.trim());
    }
  };

  // Email reports preference handler
  const handleEmailReportsChange = async (checked) => {
    setEmailReportsEnabled(checked);
    try {
      await userService.updateProfile({
        profile: { emailReportsEnabled: checked }
      });
      await refreshProfile();
      showNotification(
        checked ? 'Vous recevrez vos rapports par email' : 'Vous ne recevrez plus vos rapports par email',
        'success'
      );
    } catch (error) {
      log.error('Error updating email reports preference:', error);
      setEmailReportsEnabled(!checked); // Revert on error
      showNotification('Erreur lors de la mise à jour', 'error');
    }
  };

  // Language change handlers
  const handleLanguagePreferenceChange = async (e) => {
    const newLangCode = e.target.value;
    const newLangName = LanguagesList.keyForCode(newLangCode) || newLangCode;
    setLanguagePreference(newLangName);
    
    // For bronze users, sync both languages
    let updates = {
      profile: {
        languagePreference: newLangName
      }
    };
    
    if (profile.isBronze) {
      setDictationLanguage(newLangCode);
      updates.profile.dictationLanguage = newLangCode;
    }
    
    try {
      await userService.updateProfile(updates);
      await refreshProfile();
      showNotification('Langue de préférence mise à jour', 'success');

    } catch (error) {
      log.error('Error updating language preference:', error);
      showNotification('Erreur lors de la mise à jour', 'error');
      // Revert on error
      if (profile) {
        setLanguagePreference(profile.languagePreference || 'français');
        if (profile.isBronze) {
          const langCode = LanguagesList.languages[profile.languagePreference] || 'fr';
          setDictationLanguage(langCode);
        }
      }
    }
  };

  const handleDictationLanguageChange = async (e) => {
    const newLangCode = e.target.value;
    setDictationLanguage(newLangCode);
    
    // For bronze users, sync both languages
    let updates = {
      profile: {
        dictationLanguage: newLangCode
      }
    };
    
    if (profile.isBronze) {
      const newLangName = LanguagesList.keyForCode(newLangCode) || newLangCode;
      setLanguagePreference(newLangName);
      updates.profile.languagePreference = newLangName;
    }
    
    try {
      await userService.updateProfile(updates);
      await refreshProfile();
      showNotification('Langue de dictée mise à jour', 'success');
    } catch (error) {
      log.error('Error updating dictation language:', error);
      showNotification('Erreur lors de la mise à jour', 'error');
      // Revert on error
      if (profile) {
        setDictationLanguage(profile.dictationLanguage || 'fr');
        if (profile.isBronze) {
          const langName = LanguagesList.keyForCode(profile.dictationLanguage) || 'français';
          setLanguagePreference(langName);
        }
      }
    }
  };

  // Enterprise management
  const handleLeaveEnterprise = async (enterpriseId, enterpriseName) => {
    if (!window.confirm(`Quitter ${enterpriseName} ?`)) return;
    
    setLeavingEnterprise(enterpriseId);
    try {
      await enterpriseService.leaveEnterprise(enterpriseId);
      setEnterprises(enterprises.filter(e => e.id !== enterpriseId));
   
      showNotification("Vous avez quitté l'organisation", 'success');
      
      // Reload profile to get updated enterprise data
      const p = await refreshProfile();
      setEnterprises(p?.enterprises || []);
    } catch (error) {
      showNotification('Erreur lors de la sortie', 'error');
    } finally {
      setLeavingEnterprise(null);
    }
  };


  // Account management
  const handleLogoutAllDevices = async () => {
    if (!window.confirm('Se déconnecter de tous les appareils ?')) return;
    
    try {
      await userService.logoutAll();
      await handleLogout();
      showNotification('Déconnexion réussie', 'success');
    } catch (error) {
      showNotification('Erreur lors de la déconnexion', 'error');
    }
  };

  const handleToggleDeleteAccount = async () => {
    if (showDeleteAccount) {
      // Cancel - reset form
      setShowDeleteAccount(false);
      setDeleteAccountForm({ phone: '', code: '', pin: '' });
      setDeleteAccountError(null);
    } else {
      // Open form and send code immediately
      setDeleteAccountError(null);
      setShowDeleteAccount(true);
      // Prefill phone with current account phone if available
      setDeleteAccountForm({ phone: phoneNumber || profile?.phone || '', code: '', pin: '' });

      try {
        await userService.startPhoneAuthProtected('sms');
        showNotification('Code de vérification envoyé par SMS', 'success');
      } catch (err) {
        const errorMsg = err?.response?.data?.message || 'Erreur lors de l\'envoi du code';
        setDeleteAccountError(errorMsg);
        showNotification(errorMsg, 'error');
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountForm.code || !deleteAccountForm.pin) {
      setDeleteAccountError('Veuillez remplir tous les champs');
      return;
    }

    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir supprimer définitivement votre compte ? Cette action est irréversible.'
    );

    if (!confirmed) return;

    setDeleteAccountLoading(true);
    setDeleteAccountError(null);

    try {
      await userService.deleteAccount(deleteAccountForm.phone || phoneNumber, deleteAccountForm.code, deleteAccountForm.pin);
      showNotification('Compte supprimé. Redirection...', 'success');
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Erreur lors de la suppression';
      setDeleteAccountError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  // Sessions handlers
  const handleToggleSessions = () => {
    setShowSessionsSection(prev => !prev);
  };

  const handleRevokeSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm('Révoquer cette session ?')) return;
    setRevokingSessionId(sessionId);
    try {
      await userService.revokeSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      showNotification('Session révoquée', 'success');
    } catch (err) {
      log.error('Error revoking session:', err);
      showNotification('Erreur lors de la révocation', 'error');
    } finally {
      setRevokingSessionId(null);
    }
  };

  // Email change handlers
  const handleToggleChangeEmail = () => {
    if (showChangeEmail) {
      // Cancel - reset form
      setShowChangeEmail(false);
      setChangeEmailForm({ newEmail: '', pin: '', code: '', emailCode: '' });
      setChangeEmailError(null);
      setChangeEmailSuccess(false);
      setEmailCodeSent(false);
    } else {
      // Just open form - don't send SMS yet
      setChangeEmailError(null);
      setShowChangeEmail(true);
      setEmailCodeSent(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!changeEmailForm.newEmail) {
      setChangeEmailError('Veuillez entrer un nouvel email');
      return;
    }

    setChangeEmailLoading(true);
    setChangeEmailError(null);

    try {
      // 1) Send verification code to the new email
      await userService.sendEmailChangeCode(changeEmailForm.newEmail);
      // 2) Also send SMS code to the current phone
      await userService.startPhoneAuthProtected('sms');
      setEmailCodeSent(true);
      showNotification('Codes envoyés à votre email et par SMS', 'success');
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erreur lors de l\'envoi des codes';
      setChangeEmailError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setChangeEmailLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!changeEmailForm.newEmail || !changeEmailForm.emailCode || !changeEmailForm.code || !changeEmailForm.pin) {
      setChangeEmailError('Veuillez remplir tous les champs');
      return;
    }

    setChangeEmailLoading(true);
    setChangeEmailError(null);

    try {
      await userService.completeEmailChange(
        changeEmailForm.newEmail,
        changeEmailForm.emailCode,
        changeEmailForm.code,
        changeEmailForm.pin
      );

      setChangeEmailSuccess(true);
      showNotification('Email modifié avec succès', 'success');

      // Refresh profile to update email display
      await refreshProfile();

      // Reset form after delay
      setTimeout(() => {
        setShowChangeEmail(false);
        setChangeEmailForm({ newEmail: '', pin: '', code: '', emailCode: '' });
        setChangeEmailSuccess(false);
        setEmailCodeSent(false);
      }, 2000);
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erreur lors de la modification';
      setChangeEmailError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setChangeEmailLoading(false);
    }
  };

  // Phone change handlers
  const handleToggleChangePhone = () => {
    if (showChangePhone) {
      // Cancel - reset form
      setShowChangePhone(false);
      setChangePhoneForm({ newPhone: '', emailCode: '', newPhoneCode: '', pin: '' });
      setChangePhoneError(null);
      setChangePhoneSuccess(false);
      setPhoneEmailCodeSent(false);
    } else {
      // Just open form - don't send code yet
      setChangePhoneError(null);
      setShowChangePhone(true);
      setPhoneEmailCodeSent(false);
    }
  };

  const handleSendPhoneChangeCode = async () => {
    if (!changePhoneForm.newPhone) {
      setChangePhoneError('Veuillez entrer un nouveau numéro de téléphone');
      return;
    }

    setChangePhoneLoading(true);
    setChangePhoneError(null);

    try {
      // Send email code and start SMS to new phone
      await userService.sendPhoneChangeEmailCode();
      await userService.startPhoneAuth(changePhoneForm.newPhone, 'sms');
      setPhoneEmailCodeSent(true);
      showNotification('Codes envoyés par email et SMS au nouveau numéro', 'success');
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Erreur lors de l\'envoi des codes';
      setChangePhoneError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setChangePhoneLoading(false);
    }
  };

  const handleChangePhone = async () => {
    if (!changePhoneForm.emailCode || !changePhoneForm.newPhoneCode || !changePhoneForm.pin) {
      setChangePhoneError('Veuillez remplir tous les champs');
      return;
    }

    setChangePhoneLoading(true);
    setChangePhoneError(null);

    try {
      await userService.changePhone(
        changePhoneForm.emailCode,
        changePhoneForm.newPhone,
        changePhoneForm.newPhoneCode,
        changePhoneForm.pin
      );

      setChangePhoneSuccess(true);
      showNotification('Numéro de téléphone modifié avec succès', 'success');

      // Refresh profile to update phone display
      await refreshProfile();

      // Reset form after delay
      setTimeout(() => {
        setShowChangePhone(false);
        setChangePhoneForm({ newPhone: '', emailCode: '', newPhoneCode: '', pin: '' });
        setChangePhoneSuccess(false);
        setPhoneEmailCodeSent(false);
      }, 2000);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Erreur lors de la modification';
      setChangePhoneError(errorMsg);
      showNotification(errorMsg, 'error');
    } finally {
      setChangePhoneLoading(false);
    }
  };

  // Trusted devices handlers
  const handleToggleTrustedDevices = () => {
    setShowTrustedDevices(prev => !prev);
  };

  const handleRemoveDevice = async (deviceId, deviceName) => {
    if (!window.confirm(`Supprimer l'appareil "${deviceName}" ?`)) return;

    setRemovingDeviceId(deviceId);
    try {
      await biometricService.removeTrustedDevice(deviceId);
      setTrustedDevices(prev => prev.filter(d => d.id !== deviceId));
      showNotification('Appareil supprimé', 'success');
    } catch (err) {
      log.error('Error removing device:', err);
      showNotification('Erreur lors de la suppression', 'error');
    } finally {
      setRemovingDeviceId(null);
    }
  };



  // Check if profile is loaded - but don't show error during initial loading
  if (!profile && !isInitialLoading) {
    return (
      <div className="settings-container">
        <div className="error-container">
          <div className="error-message">
            Impossible de charger les données du profil. 
            <button onClick={handleRefreshProfile} className="retry-button">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }




    // Early return if still loading or no profile
    if (isInitialLoading || !profile) {
      return (
          <>
            <SkeletonManager active={true} />
          </>
      );
    }
  

  return (
    <div className="settings-container">
      <BackButton />
      
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}


      {/* LANGUES Section */}
      <Card className="card-large">
        <SectionHeader icon={FaLanguage} title="Préférences" variant="languages" />

        <div className="languages-container">
          {profile.isBronze && (
            <div className="language-info-box-enhanced">
              <div className="info-icon">
                <FaLock size={20} />
              </div>
              <div className="info-content">
                <h4>Limitation du plan actuel</h4>
                <p>
                  {profile.isBronze ? (
                    <>Avec le plan Bronze, les langues de préférence et de dictée sont synchronisées. 
                    Passez au plan Silver ou Gold pour les personnaliser séparément.</>
                  ) : (
                    <>Cette fonctionnalité n'est pas disponible avec votre abonnement actuel.</>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="form-fields-grid">
            <div className="form-field">
              <label htmlFor="languagePreference" className="form-label-enhanced">
                <FaGlobe size={16} /> Langue des rapports
              </label>
              <select 
                id="languagePreference"
                className="form-select-enhanced"
                value={LanguagesList.languages[languagePreference] || languagePreference} 
                onChange={handleLanguagePreferenceChange}
              >
                {Object.entries(LanguagesList.languages).map(([name, code]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}form-field-helper
              </select>
              <small className="form-field-helper">
                La langue dans laquelle vos rapports médicaux seront générés
              </small>
            </div>

            <div className="form-field">
              <label htmlFor="dictationLanguage" className="form-label-enhanced">
                <FaMicrophone size={16} /> Langue de dictée
                  {profile.isBronze && (
                  <span className="upgrade-badge" style={{ marginLeft: '8px' }}>
                    <FaLock size={10} /> Premium
                  </span>
                )}
              </label>
              <select 
                id="dictationLanguage"
                className={`form-select-enhanced ${(profile.isBronze) ? 'disabled' : ''}`}
                value={dictationLanguage} 
                onChange={handleDictationLanguageChange}
                disabled={profile.isBronze}
              >
                {Object.entries(LanguagesList.languages).map(([name, code]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
              <small className="form-field-helper">
                La langue que vous parlez lors de vos enregistrements
              </small>
            </div>
          </div>
        </div>

        {/* Picker de spécialité déplacé ici */}
        <div className="specialty-section-enhanced" style={{ marginTop: 24 }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaStethoscope size={16} /> Spécialité
          </h4>
          <div className="specialty-grid">
            <div className="specialty-field full-width">
            
              <select
                id="specialization"
                value={showCustomSpecialty ? 'other' : specialization}
                onChange={handleSpecializationChange}
                className="form-select-enhanced"
              >
                <option value="">Sélectionnez une spécialité</option>
                {specialties.map((specialty, index) => (
                  <option key={index} value={specialty}>
                    {specialty}
                  </option>
                ))}
                <option value="other">Autre (saisie libre)</option>
              </select>
              {showCustomSpecialty && (
                <input
                  type="text"
                  className="form-input-enhanced"
                  placeholder="Entrez votre spécialité"
                  value={customSpecialty}
                  onChange={handleCustomSpecialtyChange}
                  onBlur={handleCustomSpecialtyBlur}
                  style={{ marginTop: '10px' }}
                />
              )}
              <small className="form-field-helper" style={{ marginTop: '8px' }}>
                Modifie le comportement des IAs et les modèles de rapports disponibles pour améliorer la qualité des rapports
              </small>
            </div>
          </div>
        </div>

        {/* Email Reports Toggle */}
        <div className="specialty-section-enhanced" style={{ marginTop: 24 }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaEnvelope size={16} /> Notifications
          </h4>
          <div className="settings-link-row" style={{ cursor: 'default' }}>
            <div className="settings-link-left">
              <div className="settings-link-icon">
                <FaEnvelope />
              </div>
              <div>
                <div className="settings-link-title">Recevoir les rapports par email</div>
                <div className="settings-link-subtitle">
                  Recevez une copie de vos rapports par email après chaque génération
                </div>
              </div>
            </div>
            <Toggle
              checked={emailReportsEnabled}
              onChange={handleEmailReportsChange}
            />
          </div>
        </div>
      </Card>


      {/* RACCOURCIS CLAVIER Section (Electron only) */}
      {isElectron && (
        <Card className="card-large">
          <SectionHeader icon={FaKeyboard} title="Raccourcis clavier" variant="shortcuts" />

          <div className="shortcuts-section-content" style={{ marginTop: 20 }}>
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <Button
                  type="fixed"
                  variant="primary"
                  onClick={saveShortcuts}
                  disabled={!shortcutsModified || !!recordingShortcut}
                  icon={FaSave}
                  size="small"
                >
                  Enregistrer
                </Button>
                <Button
                  type="fixed"
                  variant="secondary"
                  onClick={resetShortcuts}
                  icon={FaUndo}
                  size="small"
                >
                  Réinitialiser
                </Button>
                {recordingShortcut && (
                  <Button
                    type="fixed"
                    variant="secondary"
                    onClick={() => stopRecordingShortcut({ ensureDisabledIfEmpty: true })}
                    icon={FaTimes}
                    size="small"
                  >
                    Annuler la saisie
                  </Button>
                )}
              </div>

              {/* Shortcuts table */}
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #DC143C' }}>
                      <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.9rem' }}>
                        Fonction
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', fontSize: '0.9rem' }}>
                        Raccourci
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Primary shortcuts - always visible */}
                    {Object.entries(shortcuts)
                      .filter(([id]) => primaryShortcutIds.includes(id))
                      .map(([id, shortcut]) => (
                      <tr key={id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                          {shortcut.desc}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              onClick={() => { if (recordingShortcut || shortcut.disabled) return; startRecordingShortcut(id); }}
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: shortcut.disabled ? '#9e9e9e' : (recordingShortcut === id ? '#FF6B6B' : '#DC143C'),
                                color: 'white',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: shortcut.disabled ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              {shortcut.disabled ? 'désactivé' : (recordingShortcut === id
                                ? 'ESC pour annuler'
                                : (shortcut.key || '').replace('CommandOrControl', 'Ctrl'))}
                            </div>
                            <Button
                              type="inversed"
                              variant={shortcut.disabled ? 'secondary' : 'danger'}
                              icon={FaBan}
                              size="small"
                              onClick={() => {
                                const wasDisabled = !!shortcuts[id]?.disabled;
                                if (recordingShortcut === id) {
                                  try { stopRecordingShortcut(); } catch (_) {}
                                }
                                setShortcuts(prev => ({
                                  ...prev,
                                  [id]: { ...prev[id], disabled: !prev[id].disabled, key: '' }
                                }));
                                setShortcutsModified(true);
                                if (wasDisabled) {
                                  // Defer to next frame to ensure React state is committed
                                  setTimeout(() => startRecordingShortcut(id, true), 50);
                                }
                              }}
                            >
                              {shortcut.disabled ? 'Activer' : 'Désactiver'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {/* Advanced shortcuts - shown when expanded */}
                    {showAdvancedShortcuts && Object.entries(shortcuts)
                      .filter(([id]) => !primaryShortcutIds.includes(id))
                      .map(([id, shortcut]) => (
                      <tr key={id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                          {shortcut.desc}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              onClick={() => { if (recordingShortcut || shortcut.disabled) return; startRecordingShortcut(id); }}
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: shortcut.disabled ? '#9e9e9e' : (recordingShortcut === id ? '#FF6B6B' : '#DC143C'),
                                color: 'white',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: shortcut.disabled ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              {shortcut.disabled ? 'désactivé' : (recordingShortcut === id
                                ? 'ESC pour annuler'
                                : (shortcut.key || '').replace('CommandOrControl', 'Ctrl'))}
                            </div>
                            <Button
                              type="inversed"
                              variant={shortcut.disabled ? 'secondary' : 'danger'}
                              icon={FaBan}
                              size="small"
                              onClick={() => {
                                const wasDisabled = !!shortcuts[id]?.disabled;
                                if (recordingShortcut === id) {
                                  try { stopRecordingShortcut(); } catch (_) {}
                                }
                                setShortcuts(prev => ({
                                  ...prev,
                                  [id]: { ...prev[id], disabled: !prev[id].disabled, key: '' }
                                }));
                                setShortcutsModified(true);
                                if (wasDisabled) {
                                  // Defer to next frame to ensure React state is committed
                                  setTimeout(() => startRecordingShortcut(id, true), 50);
                                }
                              }}
                            >
                              {shortcut.disabled ? 'Activer' : 'Désactiver'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Show more/less button */}
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button
                  type="inversed"
                  variant="secondary"
                  onClick={() => setShowAdvancedShortcuts(!showAdvancedShortcuts)}
                  icon={showAdvancedShortcuts ? FaChevronUp : FaChevronDown}
                  size="small"
                >
                  {showAdvancedShortcuts ? 'Masquer les raccourcis avancés' : 'Afficher plus de raccourcis'}
                </Button>
              </div>

              <div style={{
                marginTop: 20,
                padding: 15,
                background: 'rgba(220,20,60,0.05)',
                borderRadius: 8,
                fontSize: '0.85rem',
                color: '#586069'
              }}>
                <FaInfoCircle style={{ marginRight: 8 }} />
                Cliquez sur un raccourci pour le modifier. Les changements prennent effet après avoir cliqué sur "Enregistrer".
              </div>
          </div>
        </Card>
      )}
      
      {/* Organisations section moved to Home */}

      {/* GESTION DU COMPTE Section */}
      <Card className="card-large">
        <SectionHeader icon={FaUser} title="Gestion du compte" variant="account" />
        <div className="account-actions-enhanced" style={{ marginTop: 20 }}>

          {/* Email Management - Single Line */}
          <div className="settings-link-row" onClick={handleToggleChangeEmail} style={{ marginBottom: 16 }}>
            <div className="settings-link-left">
              <div className="settings-link-icon">
                <FaEnvelope />
              </div>
              <div>
                <div className="settings-link-title">Email</div>
                <div className="settings-link-subtitle">
                  {profile?.email || 'Aucun email configuré'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {showChangeEmail ? 'Annuler' : 'Modifier'}
              </span>
              <FaChevronRight />
            </div>
          </div>

          {/* Change Email Form */}
          {showChangeEmail && !changeEmailSuccess && (
            <div style={{ marginBottom: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
              <div style={{ marginBottom: 16 }}>
                <Input
                  id="new-email"
                  type="email"
                  label="Nouvel email"
                  value={changeEmailForm.newEmail}
                  onChange={e => setChangeEmailForm({ ...changeEmailForm, newEmail: e.target.value })}
                  placeholder="nouveau.email@example.com"
                  disabled={changeEmailLoading || emailCodeSent}
                />
              </div>

              {!emailCodeSent && (
                <Button
                  variant="secondary"
                  onClick={handleSendEmailCode}
                  disabled={!changeEmailForm.newEmail || changeEmailLoading}
                  icon={changeEmailLoading ? FaSpinner : FaPaperPlane}
                  isSpinning={changeEmailLoading}
                  style={{ marginBottom: 16 }}
                >
                  {changeEmailLoading ? 'Envoi...' : 'Continuer'}
                </Button>
              )}

              {emailCodeSent && (
                <>
                  <p style={{ marginBottom: '16px', color: '#2e7d32', fontSize: '14px', fontWeight: 500 }}>
                    ✓ Codes envoyés à votre email et par SMS
                  </p>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="email-verification-code"
                      type="text"
                      label="Code de vérification (Email)"
                      value={changeEmailForm.emailCode}
                      onChange={e => setChangeEmailForm({ ...changeEmailForm, emailCode: e.target.value })}
                      placeholder="123456"
                      disabled={changeEmailLoading}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="verification-code"
                      type="text"
                      label="Code de vérification (SMS)"
                      value={changeEmailForm.code}
                      onChange={e => setChangeEmailForm({ ...changeEmailForm, code: e.target.value })}
                      placeholder="123456"
                      disabled={changeEmailLoading}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="pin-verification"
                      type="password"
                      label="Code PIN (6 chiffres)"
                      value={changeEmailForm.pin}
                      onChange={e => setChangeEmailForm({ ...changeEmailForm, pin: e.target.value })}
                      placeholder="123456"
                      maxLength={6}
                      disabled={changeEmailLoading}
                    />
                  </div>

                  <Button
                    variant="primary"
                    onClick={handleChangeEmail}
                    disabled={!changeEmailForm.code || !changeEmailForm.pin || changeEmailLoading}
                    icon={changeEmailLoading ? FaSpinner : FaCheck}
                    isSpinning={changeEmailLoading}
                  >
                    {changeEmailLoading ? 'Modification...' : 'Confirmer le changement'}
                  </Button>
                </>
              )}

              {changeEmailError && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#ffebee',
                  borderRadius: '8px',
                  color: '#c62828'
                }}>
                  {changeEmailError}
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {changeEmailSuccess && (
            <div style={{
              marginBottom: '24px',
              marginLeft: '16px',
              marginRight: '16px',
              padding: '16px',
              background: '#e8f5e9',
              borderRadius: '8px',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaCheck /> Email modifié avec succès !
            </div>
          )}

          {/* Phone Management - Single Line */}
          <div className="settings-link-row" onClick={handleToggleChangePhone} style={{ marginBottom: 16 }}>
            <div className="settings-link-left">
              <div className="settings-link-icon">
                <FaPhone />
              </div>
              <div>
                <div className="settings-link-title">Numéro de téléphone</div>
                <div className="settings-link-subtitle">
                  {profile?.phone || phoneNumber || 'Aucun numéro configuré'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {showChangePhone ? 'Annuler' : 'Modifier'}
              </span>
              <FaChevronRight />
            </div>
          </div>

          {/* Change Phone Form */}
          {showChangePhone && !changePhoneSuccess && (
            <div style={{ marginBottom: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
              <div style={{ marginBottom: 16 }}>
                <Input
                  id="new-phone"
                  type="tel"
                  label="Nouveau numéro de téléphone"
                  value={changePhoneForm.newPhone}
                  onChange={e => setChangePhoneForm({ ...changePhoneForm, newPhone: e.target.value })}
                  placeholder="+41790000000"
                  disabled={changePhoneLoading || phoneEmailCodeSent}
                />
              </div>

              {!phoneEmailCodeSent && (
                <Button
                  variant="secondary"
                  onClick={handleSendPhoneChangeCode}
                  disabled={!changePhoneForm.newPhone || changePhoneLoading}
                  icon={changePhoneLoading ? FaSpinner : FaPaperPlane}
                  isSpinning={changePhoneLoading}
                  style={{ marginBottom: 16 }}
                >
                  {changePhoneLoading ? 'Envoi...' : 'Continuer'}
                </Button>
              )}

              {phoneEmailCodeSent && (
                <>
                  <p style={{ marginBottom: '16px', color: '#2e7d32', fontSize: '14px', fontWeight: 500 }}>
                    ✓ Code envoyé à votre email et par SMS au nouveau numéro
                  </p>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="email-code"
                      type="text"
                      label="Code de vérification (Email)"
                      value={changePhoneForm.emailCode}
                      onChange={e => setChangePhoneForm({ ...changePhoneForm, emailCode: e.target.value })}
                      placeholder="123456"
                      disabled={changePhoneLoading}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="new-phone-code"
                      type="text"
                      label="Code de vérification (SMS nouveau numéro)"
                      value={changePhoneForm.newPhoneCode}
                      onChange={e => setChangePhoneForm({ ...changePhoneForm, newPhoneCode: e.target.value })}
                      placeholder="123456"
                      disabled={changePhoneLoading}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Input
                      id="pin-verification-phone"
                      type="password"
                      label="Code PIN (6 chiffres)"
                      value={changePhoneForm.pin}
                      onChange={e => setChangePhoneForm({ ...changePhoneForm, pin: e.target.value })}
                      placeholder="123456"
                      maxLength={6}
                      disabled={changePhoneLoading}
                    />
                  </div>

                  <Button
                    variant="primary"
                    onClick={handleChangePhone}
                    disabled={!changePhoneForm.emailCode || !changePhoneForm.newPhoneCode || !changePhoneForm.pin || changePhoneLoading}
                    icon={changePhoneLoading ? FaSpinner : FaCheck}
                    isSpinning={changePhoneLoading}
                  >
                    {changePhoneLoading ? 'Modification...' : 'Confirmer le changement'}
                  </Button>
                </>
              )}

              {changePhoneError && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#ffebee',
                  borderRadius: '8px',
                  color: '#c62828'
                }}>
                  {changePhoneError}
                </div>
              )}
            </div>
          )}

          {/* Phone Change Success Message */}
          {changePhoneSuccess && (
            <div style={{
              marginBottom: '24px',
              marginLeft: '16px',
              marginRight: '16px',
              padding: '16px',
              background: '#e8f5e9',
              borderRadius: '8px',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaCheck /> Numéro de téléphone modifié avec succès !
            </div>
          )}

            {/* Trusted Devices Section - Works on all platforms */}
            <div className="settings-link-row" onClick={handleToggleTrustedDevices} style={{ marginBottom: 16 }}>
              <div className="settings-link-left">
                <div className="settings-link-icon">
                  <FaShieldAlt />
                </div>
                <div>
                  <div className="settings-link-title">Appareils de confiance</div>
                  <div className="settings-link-subtitle">
                    {trustedDevices.length} appareil{trustedDevices.length > 1 ? 's' : ''} enregistré{trustedDevices.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {showTrustedDevices ? 'Masquer' : 'Voir'}
                </span>
                <FaChevronRight />
              </div>
            </div>

            {showTrustedDevices && (
              <div style={{ marginBottom: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
                {trustedDevicesLoading ? (
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <FaSpinner className="fa-spin" /> Chargement...
                  </div>
                ) : trustedDevices.length === 0 ? (
                  <div style={{
                    padding: '16px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    Aucun appareil de confiance enregistré
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {trustedDevices.map((device) => (
                      <div key={device.id} style={{
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                            {device.deviceName || device.device_name || 'Appareil'}
                          </p>
                          <small style={{ color: '#666' }}>
                            {device.platform === 'ios' ? '📱 iOS' :
                             device.platform === 'android' ? '📱 Android' :
                             device.platform === 'web' ? '🌐 Web' :
                             device.platform === 'desktop' ? '💻 Desktop' : device.platform}
                            {' • '}
                            {device.createdAt || device.created_at ?
                              new Date(device.createdAt || device.created_at).toLocaleDateString('fr-FR') : '-'}
                          </small>
                        </div>
                        <Button
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDevice(
                              device.id,
                              device.deviceName || device.device_name || 'cet appareil'
                            );
                          }}
                          disabled={removingDeviceId === device.id}
                          icon={removingDeviceId === device.id ? FaSpinner : FaTrash}
                          size="small"
                          isSpinning={removingDeviceId === device.id}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sessions Section */}
            <div className="settings-link-row" onClick={handleToggleSessions} style={{ marginBottom: 16 }}>
              <div className="settings-link-left">
                <div className="settings-link-icon">
                  <FaLock />
                </div>
                <div>
                  <div className="settings-link-title">Sessions de connexion</div>
                  <div className="settings-link-subtitle">
                    {sessions.length} session{sessions.length > 1 ? 's' : ''} active{sessions.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {showSessionsSection ? 'Masquer' : 'Voir'}
                </span>
                <FaChevronRight />
              </div>
            </div>

            {showSessionsSection && (
              <div style={{ marginBottom: '24px', paddingLeft: '16px', paddingRight: '16px' }}>
                {/* Logout All Button - Above Sessions List */}
                <Button
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogoutAllDevices();
                  }}
                  icon={FaSignOutAlt}
                  style={{ marginBottom: 16, width: '100%' }}
                >
                  Se déconnecter de tous les appareils
                </Button>

                {sessionsLoading ? (
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <FaSpinner className="fa-spin" /> Chargement...
                  </div>
                ) : sessions.length === 0 ? (
                  <div style={{
                    padding: '16px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    Aucune session active
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {sessions.map((s) => (
                      <div key={s.id} style={{
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                            {s.device_name || s.deviceName || 'Appareil'}
                          </p>
                          <small style={{ color: '#666' }}>
                            Créée: {s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : '-'}
                            {' • '}
                            Expire: {s.expires_at ? new Date(s.expires_at).toLocaleDateString('fr-FR') : '-'}
                          </small>
                        </div>
                        <Button
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevokeSession(s.id);
                          }}
                          disabled={revokingSessionId === s.id}
                          icon={revokingSessionId === s.id ? FaSpinner : FaTrash}
                          size="small"
                          isSpinning={revokingSessionId === s.id}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

              {/* Delete Account - NavLink style */}
              <div className="settings-link-row" onClick={handleToggleDeleteAccount} style={{ borderColor: 'var(--color-danger)', marginBottom: 16 }}>
                <div className="settings-link-left">
                  <div className="settings-link-icon" style={{ color: 'var(--color-danger)' }}>
                    <FaTrash />
                  </div>
                  <div>
                    <div className="settings-link-title" style={{ color: 'var(--color-danger)' }}>
                      {showDeleteAccount ? 'Annuler la suppression' : 'Supprimer votre compte'}
                    </div>
                    <div className="settings-link-subtitle">Toutes vos données seront définitivement supprimées directement sans aucune rétention</div>
                  </div>
                </div>
                <FaChevronRight />
              </div>

              {showDeleteAccount && (
              <div className="delete-account-section-enhanced">
                <div className="danger-warning">
                  <FaSkull className="warning-icon" size={48} />
                  <div className="warning-content">
                    <h4>Zone dangereuse</h4>
                    <p>
                      <strong>Cette action est irréversible !</strong><br/>
                      Toutes vos données, rapports, transcriptions et paramètres seront définitivement supprimés. GBM Medical SA ne gardera aucune donnée personnelle après suppression. <br/>
                      <strong>Aucun remboursement de votre abonnement en cours n'est possible.</strong>
                    </p>
                    <p style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
                      Un code de vérification a été envoyé à votre numéro de téléphone.
                    </p>
                  </div>
                </div>

                <div className="confirmation-steps">
                  <div className="step-item">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <label className="form-label-enhanced">
                        <FaPhone size={16} />
                        Numéro de téléphone (E.164)
                      </label>
                      <input
                        type="tel"
                        className="form-input-danger"
                        value={deleteAccountForm.phone}
                        onChange={(e) => setDeleteAccountForm({ ...deleteAccountForm, phone: e.target.value })}
                        placeholder="+41790000000"
                        disabled={deleteAccountLoading}
                      />
                    </div>
                  </div>

                  <div className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <label className="form-label-enhanced">
                        <FaKey size={16} />
                        Code de vérification (SMS)
                      </label>
                      <input
                        type="text"
                        className="form-input-danger"
                        value={deleteAccountForm.code}
                        onChange={(e) => setDeleteAccountForm({ ...deleteAccountForm, code: e.target.value })}
                        placeholder="123456"
                        disabled={deleteAccountLoading}
                      />
                    </div>
                  </div>

                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <label className="form-label-enhanced">
                        <FaLock size={16} />
                        Code PIN (6 chiffres)
                      </label>
                      <input
                        type="password"
                        className="form-input-danger"
                        value={deleteAccountForm.pin}
                        onChange={(e) => setDeleteAccountForm({ ...deleteAccountForm, pin: e.target.value })}
                        placeholder="123456"
                        maxLength={6}
                        disabled={deleteAccountLoading}
                      />
                    </div>
                  </div>
                </div>

                {deleteAccountError && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#ffebee',
                    borderRadius: '8px',
                    color: '#c62828'
                  }}>
                    {deleteAccountError}
                  </div>
                )}

                <div className="final-confirmation">
                  <Button
                    type="fixed"
                    variant="danger"
                    onClick={handleDeleteAccount}
                    disabled={!deleteAccountForm.code || !deleteAccountForm.pin || deleteAccountLoading}
                    icon={deleteAccountLoading ? FaSpinner : FaTrash}
                    isSpinning={deleteAccountLoading}
                    size="large"
                  >
                   {deleteAccountLoading ? 'SUPPRESSION...' : 'SUPPRIMER DÉFINITIVEMENT VOTRE COMPTE'}
                  </Button>
                </div>
              </div>
            )}
        </div>
      </Card>

      {/* Emails section moved above account management and always visible */}

    </div>
  );
}

export default Settings;